import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWeb3 } from '../context/Web3Context';
import { toast } from 'sonner';
import { 
  ArrowLeft, Home, Shield, FileText, CheckCircle, AlertTriangle, 
  Calculator, Printer, DollarSign, Calendar, ChevronRight, Lock,
  Building, Users, Landmark, RefreshCw, Info, Scale, Pen, FileCheck, Mail, Send, Package
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { 
  generateMortgageSubmissionMemorandum, 
  generateMortgageAcceptanceForm,
  generateMortgageDeedOfTrust,
  generateTokenizedCheckDisclosure,
  generateMortgageRoutingLetter
} from '../utils/documentTemplates';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BETH_TOKEN = "0x6822824c5349bf005e353ec5dda3fa34595ea811";

// Treasury-Indexed Mortgage Obligation Products
const TIMO_PRODUCTS = [
  {
    id: 'timo-15',
    name: 'TIMO-15 (15-Year)',
    description: '15-year Treasury-indexed mortgage obligation',
    term: 180, // months
    referenceRate: 'U.S. Treasury 10-Year CMR',
    baseAPR: 5.25,
    spread: 1.50,
    minAmount: 100000,
    maxAmount: 2000000,
    features: ['BETH Credit Enhancement', 'PAS Protection', 'Faster Payoff']
  },
  {
    id: 'timo-20',
    name: 'TIMO-20 (20-Year)',
    description: '20-year Treasury-indexed mortgage obligation',
    term: 240, // months
    referenceRate: 'U.S. Treasury 10-Year CMR',
    baseAPR: 5.50,
    spread: 1.75,
    minAmount: 100000,
    maxAmount: 3000000,
    features: ['BETH Credit Enhancement', 'PAS Protection', 'Balanced Term']
  },
  {
    id: 'timo-30',
    name: 'TIMO-30 (30-Year)',
    description: '30-year Treasury-indexed mortgage obligation',
    term: 360, // months
    referenceRate: 'U.S. Treasury 30-Year CMR',
    baseAPR: 5.75,
    spread: 2.00,
    minAmount: 150000,
    maxAmount: 5000000,
    features: ['BETH Credit Enhancement', 'PAS Protection', 'Lowest Payment']
  }
];

const MortgageRefinance = () => {
  const { user, token } = useAuth();
  const { account, bethBalance } = useWeb3();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [mortgageAmount, setMortgageAmount] = useState('');
  const [costSheet, setCostSheet] = useState(null);
  const [orders, setOrders] = useState([]);
  const [novations, setNovations] = useState([]);
  const [showNovationForm, setShowNovationForm] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [selectedNovation, setSelectedNovation] = useState(null);
  const [lenderEmail, setLenderEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Form for creating new mortgage obligation
  const [formData, setFormData] = useState({
    borrower_name: '',
    borrower_wallet: '',
    property_address: '',
    property_type: 'single_family',
    original_lender: '',
    original_lender_contact: '',
    current_balance: '',
    current_rate: '',
    current_payment: '',
    payoff_amount: '',
    term_months: 360,
    beth_collateral: ''
  });
  
  // Novation form for mortgage refinancing
  const [novationForm, setNovationForm] = useState({
    original_lender_name: '',
    original_lender_contact: '',
    original_loan_number: '',
    original_principal: '',
    original_rate: '',
    original_monthly_payment: '',
    original_remaining_balance: '',
    original_maturity_date: '',
    property_address: '',
    property_type: 'single_family',
    property_value: '',
    lien_position: 'first',
    payoff_amount: '',
    effective_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchOrders();
    fetchNovations();
  }, [token]);

  useEffect(() => {
    if (account) {
      setFormData(prev => ({ ...prev, borrower_wallet: account }));
    }
  }, [account]);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({ ...prev, borrower_name: user.name || user.email }));
    }
  }, [user]);

  const fetchOrders = async () => {
    try {
      const res = await fetch(`${API}/mortgage-refinance/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setOrders(data.orders || []);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };
  
  const fetchNovations = async () => {
    try {
      const res = await fetch(`${API}/mortgage-refinance/novations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNovations(data.agreements || []);
      }
    } catch (error) {
      console.error('Error fetching novations:', error);
    }
  };
  
  const createNovationAgreement = async () => {
    if (!selectedProduct || !costSheet || !novationForm.original_lender_name) {
      toast.error('Please fill in original lender details and select a TIMO product');
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API}/mortgage-refinance/novation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...novationForm,
          borrower_name: formData.borrower_name,
          borrower_wallet: formData.borrower_wallet,
          new_principal: parseFloat(costSheet.principal),
          new_indexed_apr: parseFloat(costSheet.indexedAPR),
          new_term_months: selectedProduct.term,
          new_monthly_payment: parseFloat(costSheet.monthlyPayment),
          treasury_index: selectedProduct.referenceRate,
          beth_collateral: parseFloat(costSheet.bethCollateral)
        })
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success('Mortgage Novation Agreement created! Awaiting lender consent.');
        fetchNovations();
        setShowNovationForm(false);
        setActiveTab('novations');
        setTimeout(() => printMortgageNovationDocument(data.agreement), 500);
      } else {
        toast.error(data.error || 'Failed to create novation agreement');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error creating novation agreement');
    } finally {
      setLoading(false);
    }
  };
  
  const sendConsentRequest = async (novation) => {
    if (!lenderEmail) {
      toast.error('Please enter the lender email address');
      return;
    }
    
    setSendingEmail(true);
    try {
      const res = await fetch(`${API}/mortgage-refinance/novation/${novation.agreement_id}/send-consent-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ lender_email: lenderEmail })
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success(`Consent request email sent to ${lenderEmail}`);
        setShowConsentModal(false);
        setLenderEmail('');
        setSelectedNovation(null);
        fetchNovations(); // Refresh to show email sent status
      } else {
        toast.error(data.error || 'Failed to send consent request');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error sending consent request');
    } finally {
      setSendingEmail(false);
    }
  };
  
  const printMortgageNovationDocument = (agreement) => {
    const printContent = generateMortgageNovationHTML(agreement);
    const printWindow = window.open('', '_blank', 'width=900,height=800,scrollbars=yes');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => { try { printWindow.print(); } catch (e) { /* ignore print errors */ } }, 1000);
    }
  };

  // Generate Complete Lender Package - includes all documents for mortgage refinance
  const generateLenderPackage = (agreement) => {
    const packageId = `PKG-MORT-${Date.now().toString(36).toUpperCase()}`;
    const timestamp = new Date().toLocaleString();
    
    // Prepare data for all document templates
    const docData = {
      agreement_id: agreement?.agreement_id || 'DRAFT',
      borrower_name: agreement?.borrower_name || formData.borrower_name || '_______________',
      borrower_wallet: agreement?.borrower_wallet || formData.borrower_wallet || '0x...',
      lender_name: agreement?.original_lender_name || novationForm.original_lender_name || '_______________',
      lender_contact: agreement?.original_lender_contact || novationForm.original_lender_contact || '',
      property_address: agreement?.property_address || novationForm.property_address || formData.property_address || '_______________',
      property_type: agreement?.property_type || novationForm.property_type || 'single_family',
      property_state: (agreement?.property_address || novationForm.property_address || '').split(',').pop()?.trim() || 'Delaware',
      property_county: '',
      payoff_amount: agreement?.payoff_amount || novationForm.payoff_amount || 0,
      current_balance: agreement?.current_balance || novationForm.current_balance || 0,
      loan_number: agreement?.original_loan_number || novationForm.original_loan_number || '',
      beth_collateral: agreement?.beth_collateral || formData.beth_collateral || 0
    };
    
    // Generate each document HTML
    const routingLetterHTML = generateMortgageRoutingLetter(docData);
    const novationHTML = generateMortgageNovationHTML(agreement);
    const memorandumHTML = generateMortgageSubmissionMemorandum(docData);
    const acceptanceHTML = generateMortgageAcceptanceForm(docData);
    const deedOfTrustHTML = generateMortgageDeedOfTrust(docData);
    const checkDisclosureHTML = generateTokenizedCheckDisclosure(docData);
    
    // Extract body content from each HTML
    const extractBody = (html) => {
      const match = html.match(/<body[^>]*>([\s\S]*)<\/body>/i);
      return match ? match[1] : html;
    };
    
    // Combine into complete lender package
    const packageHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Mortgage Refinance Lender Package - ${packageId}</title>
        <style>
          @page { size: letter; margin: 0.5in; }
          body { 
            font-family: 'Georgia', serif; 
            margin: 0; 
            padding: 0;
            color: #0a1628;
          }
          .cover-page {
            text-align: center;
            padding: 100px 40px;
            min-height: 100vh;
            background: linear-gradient(180deg, #fefefe 0%, #f8f4e8 100%);
            page-break-after: always;
          }
          .cover-page h1 {
            font-size: 36px;
            letter-spacing: 6px;
            color: #0a1628;
            margin-bottom: 10px;
          }
          .cover-page .subtitle {
            font-size: 14px;
            color: #6366f1;
            letter-spacing: 3px;
          }
          .cover-page .package-title {
            font-size: 24px;
            color: #0a1628;
            border: 2px solid #6366f1;
            padding: 20px;
            display: inline-block;
            margin: 60px 0;
          }
          .cover-page .package-id {
            font-family: monospace;
            font-size: 14px;
            background: #f0f0f0;
            padding: 10px 20px;
            display: inline-block;
          }
          .toc {
            margin-top: 60px;
            text-align: left;
            max-width: 500px;
            margin-left: auto;
            margin-right: auto;
          }
          .toc h3 {
            font-size: 14px;
            color: #6366f1;
            margin-bottom: 20px;
            letter-spacing: 2px;
          }
          .toc-item {
            display: flex;
            justify-content: space-between;
            padding: 10px 0;
            border-bottom: 1px dotted #ccc;
            font-size: 12px;
          }
          .document-section {
            page-break-before: always;
          }
          .page { 
            page-break-after: always;
            min-height: 100vh;
          }
          .page:last-child {
            page-break-after: auto;
          }
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        <!-- Cover Page -->
        <div class="cover-page">
          <div style="position: absolute; top: 30px; left: 30px; width: 60px; height: 60px; border: 2px solid #0a1628; border-radius: 50%; display: flex; flex-direction: column; align-items: center; justify-content: center;">
            <span style="font-size: 8px; font-weight: bold;">OFFICIAL</span>
            <span style="font-size: 10px;">✦</span>
            <span style="font-size: 8px; font-weight: bold;">DOCUMENT</span>
          </div>
          
          <h1>BETHEREUM</h1>
          <p class="subtitle">PRIVATE WEALTH MANAGEMENT</p>
          
          <div class="package-title">
            MORTGAGE REFINANCE<br/>LENDER DOCUMENT PACKAGE
          </div>
          
          <div class="package-id">PACKAGE ID: ${packageId}</div>
          <p style="margin-top: 15px; color: #666; font-size: 12px;">Generated: ${timestamp}</p>
          
          <div class="toc">
            <h3>DOCUMENT CONTENTS</h3>
            <div class="toc-item"><span>1. Servicer Routing & Transmittal Letter</span><span>Page 2</span></div>
            <div class="toc-item"><span>2. Mortgage Novation & Settlement Agreement</span><span>Page 3</span></div>
            <div class="toc-item"><span>3. Credit Enhancement Submission Memorandum</span><span>Page 4</span></div>
            <div class="toc-item"><span>4. Mortgagee Acceptance & Acknowledgment Form</span><span>Page 5</span></div>
            <div class="toc-item"><span>5. PAS-Backed Deed of Trust</span><span>Page 6</span></div>
            <div class="toc-item"><span>6. Tokenized Check Disclosure Agreement</span><span>Page 7</span></div>
          </div>
          
          <div style="position: absolute; bottom: 40px; left: 0; right: 0; text-align: center; font-size: 10px; color: #666;">
            <p>🔒 SECURED DOCUMENT PACKAGE • ✓ BLOCKCHAIN VERIFIED • ⚡ ETHEREUM MAINNET</p>
            <p>BETH Token: ${BETH_TOKEN}</p>
          </div>
        </div>
        
        <!-- Document 1: Routing Letter -->
        <div class="document-section">
          ${routingLetterHTML}
        </div>
        
        <!-- Document 2: Novation Agreement -->
        <div class="document-section">
          ${extractBody(novationHTML)}
        </div>
        
        <!-- Document 3: Submission Memorandum -->
        <div class="document-section">
          ${memorandumHTML}
        </div>
        
        <!-- Document 4: Acceptance Form -->
        <div class="document-section">
          ${acceptanceHTML}
        </div>
        
        <!-- Document 5: Deed of Trust -->
        <div class="document-section">
          ${deedOfTrustHTML}
        </div>
        
        <!-- Document 6: Check Disclosure -->
        <div class="document-section">
          ${checkDisclosureHTML}
        </div>
      </body>
      </html>
    `;
    
    // Open in new window for printing
    const printWindow = window.open('', '_blank', 'width=900,height=800,scrollbars=yes');
    if (printWindow) {
      printWindow.document.write(packageHTML);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        try {
          printWindow.print();
          toast.success('Lender Package opened for printing');
        } catch (e) {
          toast.info('Package opened. Use Ctrl+P / Cmd+P to print.');
        }
      }, 1000);
    } else {
      toast.error('Popup blocked. Please allow popups for this site.');
    }
  };
  
  const generateMortgageNovationHTML = (agreement) => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Mortgage Novation & Settlement Agreement - ${agreement?.agreement_id || 'DRAFT'}</title>
        <style>
          @page { size: letter; margin: 0.6in; }
          body { font-family: 'Georgia', serif; margin: 0; padding: 30px; color: #0a1628; line-height: 1.7; max-width: 850px; margin: 0 auto; }
          .header { text-align: center; border-bottom: 3px double #b8860b; padding-bottom: 20px; margin-bottom: 25px; position: relative; }
          .header h1 { color: #0a1628; font-size: 26px; margin: 0; letter-spacing: 3px; }
          .header .subtitle { color: #b8860b; font-size: 10px; letter-spacing: 2px; margin-top: 5px; }
          .seal { position: absolute; top: 0; left: 0; width: 50px; height: 50px; border: 2px solid #0a1628; border-radius: 50%; display: flex; flex-direction: column; align-items: center; justify-content: center; }
          .seal span { font-size: 6px; color: #0a1628; font-weight: bold; }
          .doc-id { position: absolute; top: 5px; right: 5px; font-family: monospace; font-size: 9px; background: #f0f0f0; padding: 4px 8px; border: 1px solid #ccc; }
          .document-title { text-align: center; background: linear-gradient(180deg, #0a1628 0%, #1a2d4a 100%); color: white; padding: 12px; margin: 15px 0; font-size: 13px; letter-spacing: 2px; }
          .section { margin: 18px 0; }
          .section-title { font-size: 11px; color: #0a1628; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 10px; font-weight: bold; border-bottom: 1px solid #b8860b; padding-bottom: 5px; }
          .recital { margin: 12px 0; padding-left: 20px; font-size: 11px; }
          .recital strong { color: #0a1628; }
          .party-box { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin: 15px 0; }
          .party { padding: 12px; background: #fafafa; border-left: 3px solid #b8860b; }
          .party-label { font-size: 9px; color: #666; text-transform: uppercase; letter-spacing: 1px; }
          .party-value { font-size: 11px; color: #0a1628; margin-top: 3px; }
          .highlight-box { background: linear-gradient(135deg, #f8f4e8 0%, #fefefe 100%); border: 2px solid #b8860b; padding: 15px; margin: 18px 0; text-align: center; }
          .highlight-box .amount { font-size: 22px; font-weight: bold; color: #b8860b; }
          .signature-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 15px; margin-top: 35px; }
          .sig-box { border: 1px solid #0a1628; padding: 12px; text-align: center; }
          .sig-line { border-top: 1px solid #0a1628; width: 85%; margin: 35px auto 5px auto; }
          .sig-label { font-size: 8px; text-transform: uppercase; color: #666; }
          .legal-text { font-size: 9px; line-height: 1.6; text-align: justify; background: #f9f9f9; padding: 12px; border: 1px solid #ddd; margin: 15px 0; }
          .warning { background: #fff3cd; border: 1px solid #ffc107; padding: 10px; margin: 15px 0; font-size: 9px; color: #856404; }
          .critical { background: #f8d7da; border: 1px solid #f5c6cb; padding: 10px; margin: 15px 0; font-size: 9px; color: #721c24; }
          .footer { margin-top: 25px; padding-top: 15px; border-top: 2px solid #0a1628; font-size: 8px; color: #666; text-align: center; }
          @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="seal"><span>OFFICIAL</span><span style="font-size:9px">✦</span><span>SEAL</span></div>
          <div class="doc-id">DOC: ${agreement?.agreement_id || 'DRAFT'}</div>
          <h1>BETHEREUM</h1>
          <p class="subtitle">PRIVATE WEALTH MANAGEMENT</p>
        </div>
        
        <div class="document-title">MORTGAGE NOVATION & SETTLEMENT AGREEMENT</div>
        
        <div class="critical">
          <strong>⚠️ CRITICAL REQUIREMENT:</strong> This agreement requires the EXPRESS WRITTEN CONSENT of the Original Lender. 
          No settlement or novation occurs without lender acceptance. This is NOT a consumer mortgage refinance.
        </div>
        
        <div class="section">
          <div class="section-title">PARTIES TO THIS AGREEMENT</div>
          <div class="party-box">
            <div class="party">
              <div class="party-label">Borrower / Homeowner</div>
              <div class="party-value">${agreement?.borrower_name || formData.borrower_name || '_______________'}</div>
              <div class="party-value" style="font-family: monospace; font-size: 9px;">${agreement?.borrower_wallet || formData.borrower_wallet || '0x...'}</div>
            </div>
            <div class="party">
              <div class="party-label">Original Lender / Mortgagee</div>
              <div class="party-value">${agreement?.original_lender_name || novationForm.original_lender_name || '_______________'}</div>
              <div class="party-value">${agreement?.original_lender_contact || novationForm.original_lender_contact || ''}</div>
            </div>
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">PROPERTY</div>
          <div class="party-box">
            <div class="party" style="grid-column: span 2;">
              <div class="party-label">Property Address</div>
              <div class="party-value">${agreement?.property_address || novationForm.property_address || formData.property_address || '_______________'}</div>
            </div>
            <div class="party">
              <div class="party-label">Property Type</div>
              <div class="party-value">${(agreement?.property_type || novationForm.property_type || 'single_family').replace('_', ' ').toUpperCase()}</div>
            </div>
            <div class="party">
              <div class="party-label">Lien Position</div>
              <div class="party-value">${(agreement?.lien_position || novationForm.lien_position || 'first').toUpperCase()} LIEN</div>
            </div>
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">RECITALS</div>
          <div class="recital"><strong>WHEREAS,</strong> Borrower is currently obligated under a mortgage loan with Original Lender secured by the above-described Property;</div>
          <div class="recital"><strong>WHEREAS,</strong> the parties desire to novate (replace) said mortgage obligation with a Treasury-Indexed Mortgage Obligation (TIMO);</div>
          <div class="recital"><strong>WHEREAS,</strong> Original Lender CONSENTS to the settlement and release of the existing mortgage upon receipt of the Payoff Amount;</div>
          <div class="recital"><strong>NOW, THEREFORE,</strong> in consideration of the mutual promises herein, the parties agree as follows:</div>
        </div>
        
        <div class="section">
          <div class="section-title">ORIGINAL MORTGAGE DETAILS</div>
          <div class="party-box">
            <div class="party">
              <div class="party-label">Loan Number</div>
              <div class="party-value">${agreement?.original_loan_number || novationForm.original_loan_number || '_______________'}</div>
            </div>
            <div class="party">
              <div class="party-label">Original Principal</div>
              <div class="party-value">$${parseFloat(agreement?.original_principal || novationForm.original_principal || 0).toLocaleString()}</div>
            </div>
            <div class="party">
              <div class="party-label">Current Interest Rate</div>
              <div class="party-value">${agreement?.original_rate || novationForm.original_rate || '___'}%</div>
            </div>
            <div class="party">
              <div class="party-label">Current Monthly Payment</div>
              <div class="party-value">$${parseFloat(agreement?.original_monthly_payment || novationForm.original_monthly_payment || 0).toLocaleString()}</div>
            </div>
          </div>
        </div>
        
        <div class="highlight-box">
          <div style="font-size: 10px; color: #666; margin-bottom: 5px;">PAYOFF / SETTLEMENT AMOUNT</div>
          <div class="amount">$${parseFloat(agreement?.payoff_amount || novationForm.payoff_amount || costSheet?.principal || 0).toLocaleString()}</div>
          <div style="font-size: 10px; margin-top: 5px;">To be paid to Original Lender upon execution and lender consent</div>
        </div>
        
        <div class="section">
          <div class="section-title">NEW TREASURY-INDEXED MORTGAGE OBLIGATION (TIMO)</div>
          <div class="party-box">
            <div class="party">
              <div class="party-label">New Principal</div>
              <div class="party-value">$${parseFloat(agreement?.new_principal || costSheet?.principal || 0).toLocaleString()}</div>
            </div>
            <div class="party">
              <div class="party-label">Indexed APR</div>
              <div class="party-value">${agreement?.new_indexed_apr || costSheet?.indexedAPR || '___'}%</div>
            </div>
            <div class="party">
              <div class="party-label">Term</div>
              <div class="party-value">${Math.floor((agreement?.new_term_months || selectedProduct?.term || 360) / 12)} years</div>
            </div>
            <div class="party">
              <div class="party-label">New Monthly Payment</div>
              <div class="party-value" style="color: #b8860b; font-weight: bold;">$${parseFloat(agreement?.new_monthly_payment || costSheet?.monthlyPayment || 0).toLocaleString()}</div>
            </div>
            <div class="party">
              <div class="party-label">Treasury Index Reference</div>
              <div class="party-value">${agreement?.treasury_index || selectedProduct?.referenceRate || 'U.S. Treasury 10-Year CMR'}</div>
            </div>
            <div class="party">
              <div class="party-label">BETH Collateral Enhancement</div>
              <div class="party-value">${parseFloat(agreement?.beth_collateral || costSheet?.bethCollateral || 0).toLocaleString()} BETH</div>
            </div>
          </div>
        </div>
        
        <div class="legal-text">
          <strong>TERMS AND CONDITIONS:</strong><br/><br/>
          1. <strong>LENDER CONSENT REQUIRED:</strong> This Agreement is NOT effective until Original Lender signs the Lender Consent section below.<br/><br/>
          2. <strong>NOVATION:</strong> Upon execution and settlement, the Original Mortgage is FULLY DISCHARGED and replaced by the TIMO.<br/><br/>
          3. <strong>RELEASE OF LIEN:</strong> Original Lender shall execute and record a Release of Mortgage/Deed of Trust within 30 days of receiving the Payoff Amount.<br/><br/>
          4. <strong>NEW SECURITY INTEREST:</strong> A new security interest in favor of the SPV/Trust administering the TIMO may be recorded.<br/><br/>
          5. <strong>PAS PROTECTION:</strong> A Private Accord & Satisfaction (PAS) credit enhancement backs performance of the new obligation.<br/><br/>
          6. <strong>GOVERNING LAW:</strong> This Agreement is governed by the laws of the State of Delaware, USA.
        </div>
        
        <div class="signature-grid">
          <div class="sig-box">
            <div class="sig-label">Borrower Signature</div>
            <div class="sig-line"></div>
            <div style="font-size: 8px;">${agreement?.borrower_name || formData.borrower_name || '_______________'}</div>
            <div style="font-size: 8px; margin-top: 5px;">Date: _______________</div>
          </div>
          <div class="sig-box" style="background: #fff3cd;">
            <div class="sig-label" style="color: #856404;">Original Lender Consent (REQUIRED)</div>
            <div class="sig-line"></div>
            <div style="font-size: 8px;">${agreement?.original_lender_name || novationForm.original_lender_name || '_______________'}</div>
            <div style="font-size: 8px; margin-top: 5px;">Date: _______________</div>
          </div>
          <div class="sig-box">
            <div class="sig-label">Platform Witness</div>
            <div class="sig-line"></div>
            <div style="font-size: 8px;">Bethereum Platform</div>
            <div style="font-size: 8px; margin-top: 5px;">Date: _______________</div>
          </div>
        </div>
        
        <div class="footer">
          <p>🔒 SECURED DOCUMENT • ✓ BLOCKCHAIN VERIFIED • ⚡ ETHEREUM MAINNET</p>
          <p style="font-family: monospace;">BETH Token: ${BETH_TOKEN}</p>
          <p>Generated: ${new Date().toLocaleString()}</p>
        </div>
      </body>
      </html>
    `;
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setFormData(prev => ({ ...prev, term_months: product.term }));
    if (mortgageAmount) {
      calculateCostSheet(product, mortgageAmount);
    }
  };

  const handleAmountChange = (value) => {
    setMortgageAmount(value);
    if (selectedProduct && value) {
      calculateCostSheet(selectedProduct, value);
    } else {
      setCostSheet(null);
    }
  };

  const calculateCostSheet = (product, amount) => {
    const principal = parseFloat(amount);
    if (isNaN(principal) || principal <= 0) {
      setCostSheet(null);
      return;
    }

    const monthlyRate = product.baseAPR / 100 / 12;
    const numPayments = product.term;
    
    // Monthly payment calculation (amortization formula)
    const monthlyPayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / 
                          (Math.pow(1 + monthlyRate, numPayments) - 1);
    
    const totalPayments = monthlyPayment * numPayments;
    const totalIndexAdjustment = totalPayments - principal;
    
    // Fee structure (fixed, not performance-based)
    const structuringFee = principal * 0.015; // 1.5%
    const pasEnhancementFee = principal * 0.01; // 1%
    const adminFee = 1500; // Flat fee
    const settlementFee = 750; // Flat fee
    const titleFee = 500; // Flat fee
    const totalFees = structuringFee + pasEnhancementFee + adminFee + settlementFee + titleFee;
    
    // BETH collateral (5% of principal for mortgage - lower risk)
    const bethCollateral = principal * 0.05; // At $1.00 per BETH

    setCostSheet({
      principal: principal.toFixed(2),
      term: product.term,
      termYears: Math.floor(product.term / 12),
      indexedAPR: product.baseAPR.toFixed(2),
      monthlyPayment: monthlyPayment.toFixed(2),
      totalPayments: totalPayments.toFixed(2),
      totalIndexAdjustment: totalIndexAdjustment.toFixed(2),
      structuringFee: structuringFee.toFixed(2),
      pasEnhancementFee: pasEnhancementFee.toFixed(2),
      adminFee: adminFee.toFixed(2),
      settlementFee: settlementFee.toFixed(2),
      titleFee: titleFee.toFixed(2),
      totalFees: totalFees.toFixed(2),
      bethCollateral: bethCollateral.toFixed(0),
      collateralRatio: 5
    });
    
    setFormData(prev => ({ 
      ...prev, 
      payoff_amount: amount,
      beth_collateral: bethCollateral.toFixed(0)
    }));
  };

  const handleCreateOrder = async () => {
    if (!selectedProduct || !costSheet) {
      toast.error('Please select a product and enter mortgage amount');
      return;
    }
    
    if (!formData.property_address || !formData.original_lender) {
      toast.error('Please fill in property address and original lender information');
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API}/mortgage-refinance/order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          product_id: selectedProduct.id,
          product_name: selectedProduct.name,
          ...formData,
          principal_amount: parseFloat(costSheet.principal),
          indexed_apr: parseFloat(costSheet.indexedAPR),
          monthly_payment: parseFloat(costSheet.monthlyPayment),
          total_payments: parseFloat(costSheet.totalPayments),
          total_fees: parseFloat(costSheet.totalFees),
          beth_collateral: parseFloat(costSheet.bethCollateral)
        })
      });

      const data = await res.json();
      if (data.success) {
        toast.success('Mortgage Obligation created successfully!');
        fetchOrders();
        setActiveTab('orders');
        // Print the document
        setTimeout(() => printMortgageDocument(data.order), 500);
      } else {
        toast.error(data.error || 'Failed to create mortgage obligation');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error creating mortgage obligation');
    } finally {
      setLoading(false);
    }
  };

  const printMortgageDocument = (order) => {
    const printContent = generateMortgageDocumentHTML(order);
    const printWindow = window.open('', '_blank', 'width=900,height=800,scrollbars=yes');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        try {
          printWindow.print();
          toast.success('Document opened for printing');
        } catch (e) {
          toast.info('Document opened. Use Ctrl+P to print.');
        }
      }, 1000);
    } else {
      toast.error('Popup blocked. Please allow popups for this site.');
    }
  };

  const generateMortgageDocumentHTML = (order) => {
    const instrumentNo = order?.order_id || `TIMO-${Date.now().toString(36).toUpperCase()}`;
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Treasury-Indexed Mortgage Obligation - ${instrumentNo}</title>
        <style>
          @page { size: letter; margin: 0.5in; }
          body { 
            font-family: 'Georgia', serif; 
            margin: 0;
            padding: 30px;
            color: #0a1628;
            line-height: 1.6;
            max-width: 850px;
            margin: 0 auto;
          }
          .header {
            text-align: center;
            border-bottom: 3px double #b8860b;
            padding-bottom: 20px;
            margin-bottom: 25px;
            position: relative;
          }
          .header h1 { color: #0a1628; font-size: 28px; margin: 0; letter-spacing: 4px; }
          .header .subtitle { color: #b8860b; font-size: 10px; letter-spacing: 2px; margin-top: 5px; }
          .seal {
            position: absolute;
            top: 0;
            left: 0;
            width: 55px;
            height: 55px;
            border: 2px solid #0a1628;
            border-radius: 50%;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
          }
          .seal span { font-size: 7px; color: #0a1628; font-weight: bold; line-height: 1.2; }
          .instrument-number {
            position: absolute;
            top: 5px;
            right: 5px;
            font-family: 'Courier New', monospace;
            font-size: 9px;
            background: #f0f0f0;
            padding: 4px 8px;
            border: 1px solid #ccc;
          }
          .document-title {
            text-align: center;
            background: linear-gradient(180deg, #0a1628 0%, #1a2d4a 100%);
            color: white;
            padding: 12px;
            margin: 15px 0;
            font-size: 14px;
            letter-spacing: 2px;
          }
          .section {
            margin: 15px 0;
            padding: 12px;
            background: #fafafa;
            border-left: 4px solid #b8860b;
          }
          .section-title {
            font-size: 10px;
            color: #666;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-bottom: 8px;
            font-weight: bold;
          }
          .row {
            display: flex;
            justify-content: space-between;
            padding: 6px 0;
            border-bottom: 1px dotted #ddd;
            font-size: 11px;
          }
          .row:last-child { border-bottom: none; }
          .row .label { color: #666; }
          .row .value { font-weight: bold; color: #0a1628; }
          .highlight-box {
            background: linear-gradient(135deg, #f8f4e8 0%, #fefefe 100%);
            border: 2px solid #b8860b;
            padding: 15px;
            margin: 15px 0;
            text-align: center;
          }
          .highlight-box .amount {
            font-size: 24px;
            font-weight: bold;
            color: #b8860b;
          }
          .legal-text { 
            font-size: 9px; 
            line-height: 1.6; 
            text-align: justify; 
            background: #f9f9f9;
            padding: 15px;
            border: 1px solid #ddd;
            margin: 15px 0;
          }
          .signature-section {
            display: flex;
            gap: 20px;
            margin-top: 30px;
          }
          .sig-box {
            flex: 1;
            border: 1px solid #0a1628;
            padding: 15px;
            text-align: center;
          }
          .sig-line {
            border-top: 1px solid #0a1628;
            width: 80%;
            margin: 35px auto 5px auto;
          }
          .footer {
            margin-top: 25px;
            padding-top: 15px;
            border-top: 2px solid #0a1628;
            font-size: 8px;
            color: #666;
            text-align: center;
          }
          .warning-box {
            background: #fff3cd;
            border: 1px solid #ffc107;
            padding: 10px;
            margin: 15px 0;
            font-size: 9px;
            color: #856404;
          }
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="seal">
            <span>OFFICIAL</span>
            <span style="font-size: 10px;">✦</span>
            <span>SEAL</span>
          </div>
          <div class="instrument-number">INSTRUMENT: ${instrumentNo}</div>
          <h1>BETHEREUM</h1>
          <p class="subtitle">PRIVATE WEALTH MANAGEMENT</p>
        </div>
        
        <div class="document-title">TREASURY-INDEXED MORTGAGE OBLIGATION (TIMO)</div>
        
        <div class="warning-box">
          <strong>⚠️ IMPORTANT DISCLOSURE:</strong> This is a PRIVATE CREDIT OBLIGATION for mortgage refinancing, 
          NOT a consumer mortgage loan. This obligation replaces or restructures existing mortgage debt via private 
          contract law. Platform acts as structuring agent, not lender. Treasury rates are reference index only.
        </div>
        
        <div class="section">
          <div class="section-title">BORROWER / HOMEOWNER INFORMATION</div>
          <div class="row"><span class="label">Name</span><span class="value">${order?.borrower_name || formData.borrower_name || '_______________'}</span></div>
          <div class="row"><span class="label">Wallet Address</span><span class="value" style="font-family: monospace; font-size: 9px;">${order?.borrower_wallet || formData.borrower_wallet || '0x...'}</span></div>
        </div>
        
        <div class="section">
          <div class="section-title">PROPERTY INFORMATION</div>
          <div class="row"><span class="label">Property Address</span><span class="value">${order?.property_address || formData.property_address || '_______________'}</span></div>
          <div class="row"><span class="label">Property Type</span><span class="value">${(order?.property_type || formData.property_type || 'single_family').replace('_', ' ').toUpperCase()}</span></div>
        </div>
        
        <div class="section">
          <div class="section-title">ORIGINAL LENDER / CREDITOR INFORMATION</div>
          <div class="row"><span class="label">Lender Name</span><span class="value">${order?.original_lender || formData.original_lender || '_______________'}</span></div>
          <div class="row"><span class="label">Contact</span><span class="value">${order?.original_lender_contact || formData.original_lender_contact || '_______________'}</span></div>
          <div class="row"><span class="label">Current Balance</span><span class="value">$${parseFloat(order?.current_balance || formData.current_balance || 0).toLocaleString()}</span></div>
          <div class="row"><span class="label">Current Rate</span><span class="value">${order?.current_rate || formData.current_rate || '___'}%</span></div>
        </div>
        
        <div class="highlight-box">
          <div style="font-size: 10px; color: #666; margin-bottom: 5px;">REFINANCE / PAYOFF AMOUNT</div>
          <div class="amount">$${parseFloat(order?.principal_amount || costSheet?.principal || 0).toLocaleString()}</div>
          <div style="font-size: 11px; margin-top: 5px;">
            Term: ${costSheet?.termYears || Math.floor((order?.term_months || formData.term_months) / 12)} years | Indexed APR: ${order?.indexed_apr || costSheet?.indexedAPR}%
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">NEW PAYMENT SCHEDULE</div>
          <div class="row"><span class="label">New Monthly Payment</span><span class="value" style="color: #b8860b; font-size: 14px;">$${parseFloat(order?.monthly_payment || costSheet?.monthlyPayment || 0).toLocaleString()}</span></div>
          <div class="row"><span class="label">Number of Payments</span><span class="value">${order?.term_months || formData.term_months}</span></div>
          <div class="row"><span class="label">Total of Payments</span><span class="value">$${parseFloat(order?.total_payments || costSheet?.totalPayments || 0).toLocaleString()}</span></div>
          <div class="row"><span class="label">Index Reference</span><span class="value">${selectedProduct?.referenceRate || 'U.S. Treasury CMR'}</span></div>
        </div>
        
        <div class="section" style="background: linear-gradient(135deg, #f0f7ff 0%, #fefefe 100%);">
          <div class="section-title">CREDIT ENHANCEMENT (PAS BACKED)</div>
          <div class="row"><span class="label">BETH Collateral Required</span><span class="value">${parseFloat(order?.beth_collateral || costSheet?.bethCollateral || 0).toLocaleString()} BETH</span></div>
          <div class="row"><span class="label">Collateralization Ratio</span><span class="value">${costSheet?.collateralRatio || 5}%</span></div>
          <div class="row"><span class="label">PAS Coverage</span><span class="value">Mortgage Performance Protection</span></div>
        </div>
        
        <div class="legal-text">
          <strong>TERMS AND CONDITIONS:</strong><br/><br/>
          1. <strong>Settlement / Novation:</strong> This TIMO replaces or restructures the existing mortgage obligation via a 
          Settlement / Novation Agreement. Original lender consent is REQUIRED for this transaction to proceed.<br/><br/>
          2. <strong>Treasury Index Reference:</strong> Payment terms are indexed to publicly available U.S. Treasury benchmark rates 
          for reference purposes only. This obligation is not issued or backed by the U.S. Government.<br/><br/>
          3. <strong>PAS Credit Enhancement:</strong> A Private Accord & Satisfaction (PAS) agreement backs the TIMO with mortgage 
          performance protection, covering payment shortfalls up to a capped liability amount.<br/><br/>
          4. <strong>Title & Security:</strong> Borrower remains property owner. SPV/Trust may hold lien position. Original mortgage 
          lien may remain or be subordinated per private agreement. UCC-1 may be filed under Article 9.<br/><br/>
          5. <strong>Platform Role:</strong> Bethereum acts as a structuring and administration platform, NOT as a mortgage lender. 
          All fees are for platform services, documentation, and credit enhancement availability.<br/><br/>
          6. <strong>Governing Law:</strong> This obligation is governed by the laws of the State of Delaware, USA.
        </div>
        
        <div class="signature-section">
          <div class="sig-box">
            <div style="font-size: 9px; text-transform: uppercase;">Borrower / Homeowner Signature</div>
            <div class="sig-line"></div>
            <div style="font-size: 9px;">${order?.borrower_name || formData.borrower_name || '_______________'}</div>
            <div style="font-size: 9px; margin-top: 5px;">Date: _____________</div>
          </div>
          <div class="sig-box">
            <div style="font-size: 9px; text-transform: uppercase;">Original Lender Consent</div>
            <div class="sig-line"></div>
            <div style="font-size: 9px;">${order?.original_lender || formData.original_lender || '_______________'}</div>
            <div style="font-size: 9px; margin-top: 5px;">Date: _____________</div>
          </div>
        </div>
        
        <div class="footer">
          <p>🔒 SECURED DOCUMENT • ✓ BLOCKCHAIN VERIFIED • ⚡ ETHEREUM MAINNET</p>
          <p style="font-family: monospace;">BETH Token: ${BETH_TOKEN}</p>
          <p>Generated: ${new Date().toLocaleString()}</p>
        </div>
      </body>
      </html>
    `;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="mortgage-refinance-page">
      {/* Header */}
      <header className="border-b border-[#1a2d4a] bg-[#0d1d35]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" className="text-[#b8860b] hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-2" />
                DASHBOARD
              </Button>
            </Link>
            <div className="h-6 w-px bg-[#1a2d4a]" />
            <div>
              <h1 className="text-white text-lg font-light tracking-wide flex items-center gap-2">
                <Home className="w-5 h-5 text-[#b8860b]" />
                Mortgage Refinance Structure
              </h1>
              <p className="text-xs text-gray-500">Treasury-Indexed Mortgage Obligations (TIMO) with PAS Credit Enhancement</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {account && (
              <div className="text-right">
                <p className="text-xs text-gray-500">Connected Wallet</p>
                <p className="text-sm text-white font-mono">{account.slice(0, 8)}...{account.slice(-6)}</p>
              </div>
            )}
            <Button variant="ghost" size="sm" onClick={fetchOrders}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Disclaimer Banner */}
      <div className="bg-gradient-to-r from-yellow-900/30 to-yellow-800/30 border-b border-yellow-700/50">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center gap-3 text-yellow-200 text-xs">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <p>
              <strong>IMPORTANT:</strong> This is a PRIVATE CREDIT structure for mortgage refinancing, NOT a consumer mortgage. 
              Requires original lender consent. Platform acts as structuring agent, not lender.
            </p>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Tab Navigation */}
        <div className="flex gap-2 mb-8 flex-wrap">
          <Button
            variant={activeTab === 'overview' ? 'default' : 'outline'}
            onClick={() => setActiveTab('overview')}
            className={activeTab === 'overview' ? 'bg-[#b8860b] hover:bg-[#9a7209]' : 'border-[#1a2d4a] text-gray-400'}
            data-testid="overview-tab"
          >
            <Info className="w-4 h-4 mr-2" />
            HOW IT WORKS
          </Button>
          <Button
            variant={activeTab === 'products' ? 'default' : 'outline'}
            onClick={() => setActiveTab('products')}
            className={activeTab === 'products' ? 'bg-[#b8860b] hover:bg-[#9a7209]' : 'border-[#1a2d4a] text-gray-400'}
            data-testid="products-tab"
          >
            <Home className="w-4 h-4 mr-2" />
            TIMO PRODUCTS
          </Button>
          <Button
            variant={activeTab === 'calculator' ? 'default' : 'outline'}
            onClick={() => setActiveTab('calculator')}
            className={activeTab === 'calculator' ? 'bg-[#b8860b] hover:bg-[#9a7209]' : 'border-[#1a2d4a] text-gray-400'}
            data-testid="calculator-tab"
          >
            <Calculator className="w-4 h-4 mr-2" />
            CALCULATOR
          </Button>
          <Button
            variant={activeTab === 'orders' ? 'default' : 'outline'}
            onClick={() => setActiveTab('orders')}
            className={activeTab === 'orders' ? 'bg-[#b8860b] hover:bg-[#9a7209]' : 'border-[#1a2d4a] text-gray-400'}
            data-testid="orders-tab"
          >
            <FileText className="w-4 h-4 mr-2" />
            MY OBLIGATIONS ({orders.length})
          </Button>
          <Button
            variant={activeTab === 'novations' ? 'default' : 'outline'}
            onClick={() => setActiveTab('novations')}
            className={activeTab === 'novations' ? 'bg-indigo-600 hover:bg-indigo-700' : 'border-[#1a2d4a] text-gray-400'}
            data-testid="novations-tab"
          >
            <Scale className="w-4 h-4 mr-2" />
            NOVATION ({novations.length})
          </Button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <Card className="bg-[#0d1d35] border-[#1a2d4a]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Home className="w-5 h-5 text-[#b8860b]" />
                  What is a Treasury-Indexed Mortgage Obligation (TIMO)?
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-300 mb-4">
                  A TIMO is a <strong className="text-[#b8860b]">private credit obligation</strong> to replace or restructure an 
                  existing mortgage, backed by a <strong>PAS credit enhancement</strong> and indexed to Treasury benchmarks. 
                  This allows mortgage refinancing legally without banking or CFPB licensing.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                  <div className="p-4 bg-green-900/20 border border-green-700/50 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-green-400 mb-2" />
                    <h4 className="text-white font-medium mb-1">What You ARE Doing</h4>
                    <p className="text-xs text-gray-400">Creating a private obligation to replace/restructure existing mortgage with lender consent</p>
                  </div>
                  <div className="p-4 bg-red-900/20 border border-red-700/50 rounded-lg">
                    <AlertTriangle className="w-6 h-6 text-red-400 mb-2" />
                    <h4 className="text-white font-medium mb-1">What You ARE NOT Doing</h4>
                    <p className="text-xs text-gray-400">NOT issuing a consumer mortgage or acting as a mortgage lender</p>
                  </div>
                  <div className="p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg">
                    <Scale className="w-6 h-6 text-blue-400 mb-2" />
                    <h4 className="text-white font-medium mb-1">Critical Requirement</h4>
                    <p className="text-xs text-gray-400">Original lender MUST consent to settlement/novation</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Transaction Flow */}
            <Card className="bg-[#0d1d35] border-[#1a2d4a]">
              <CardHeader>
                <CardTitle className="text-white">Step-by-Step Transaction Flow</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { step: 1, title: 'Borrower & Lender Consent', desc: 'Borrower agrees to restructure, original lender signs novation agreement', critical: true },
                    { step: 2, title: 'Mortgage Analysis', desc: 'Collect current terms: principal, interest, escrow, maturity, lien position' },
                    { step: 3, title: 'Issue TIMO', desc: 'Create Treasury-indexed obligation matching or modifying existing maturity' },
                    { step: 4, title: 'PAS Credit Enhancement', desc: 'PAS backs the TIMO with mortgage performance protection' },
                    { step: 5, title: 'Settlement / Novation Agreement', desc: 'Defines extinguishment of original mortgage, acceptance of TIMO' },
                    { step: 6, title: 'Optional Tokenized Settlement', desc: 'Evidence of consideration, conditional, not legal tender' },
                    { step: 7, title: 'Title & Security', desc: 'Borrower remains owner, SPV/trust may hold lien, PAS referenced' },
                    { step: 8, title: 'Payment Processing', desc: 'Borrower pays per TIMO schedule, funds allocated to obligations' }
                  ].map((item) => (
                    <div key={item.step} className={`flex items-start gap-4 p-3 rounded-lg ${item.critical ? 'bg-yellow-900/20 border border-yellow-700/50' : 'bg-[#0a1628]'}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 ${item.critical ? 'bg-yellow-600' : 'bg-[#b8860b]'}`}>
                        {item.step}
                      </div>
                      <div>
                        <h4 className="text-white font-medium flex items-center gap-2">
                          {item.title}
                          {item.critical && <span className="text-xs bg-yellow-600 px-2 py-0.5 rounded">CRITICAL</span>}
                        </h4>
                        <p className="text-xs text-gray-400">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TIMO_PRODUCTS.map((product) => (
              <Card 
                key={product.id}
                className={`bg-[#0d1d35] border-[#1a2d4a] hover:border-[#b8860b] transition-colors cursor-pointer ${selectedProduct?.id === product.id ? 'border-[#b8860b] border-2' : ''}`}
                onClick={() => handleProductSelect(product)}
                data-testid={`product-${product.id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-white text-base">{product.name}</CardTitle>
                      <CardDescription className="text-gray-400 mt-1 text-xs">{product.description}</CardDescription>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-[#b8860b]">{product.baseAPR}%</div>
                      <div className="text-xs text-gray-500">Indexed APR</div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <div className="text-center p-2 bg-[#0a1628] rounded">
                      <Calendar className="w-4 h-4 mx-auto text-[#b8860b] mb-1" />
                      <div className="text-white font-bold">{Math.floor(product.term / 12)}</div>
                      <div className="text-xs text-gray-500">Years</div>
                    </div>
                    <div className="text-center p-2 bg-[#0a1628] rounded">
                      <DollarSign className="w-4 h-4 mx-auto text-[#b8860b] mb-1" />
                      <div className="text-white font-bold text-xs">{formatCurrency(product.minAmount)}</div>
                      <div className="text-xs text-gray-500">Min</div>
                    </div>
                    <div className="text-center p-2 bg-[#0a1628] rounded">
                      <Landmark className="w-4 h-4 mx-auto text-[#b8860b] mb-1" />
                      <div className="text-white font-bold text-xs">{product.referenceRate.replace('U.S. Treasury ', '').replace(' CMR', '')}</div>
                      <div className="text-xs text-gray-500">Index</div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {product.features.map((feature, idx) => (
                      <span key={idx} className="text-xs px-2 py-1 bg-[#1a2d4a] text-gray-300 rounded">
                        <CheckCircle className="w-3 h-3 inline mr-1 text-green-400" />
                        {feature}
                      </span>
                    ))}
                  </div>
                  <Button 
                    className="w-full mt-4 bg-[#b8860b] hover:bg-[#9a7209]"
                    onClick={(e) => { e.stopPropagation(); handleProductSelect(product); setActiveTab('calculator'); }}
                  >
                    Calculate <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Calculator Tab */}
        {activeTab === 'calculator' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-[#0d1d35] border-[#1a2d4a]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Home className="w-5 h-5 text-[#b8860b]" />
                  Property & Mortgage Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wider">Select Term</label>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {TIMO_PRODUCTS.map((product) => (
                      <Button
                        key={product.id}
                        variant={selectedProduct?.id === product.id ? 'default' : 'outline'}
                        className={selectedProduct?.id === product.id ? 'bg-[#b8860b]' : 'border-[#1a2d4a] text-gray-400'}
                        onClick={() => handleProductSelect(product)}
                        size="sm"
                      >
                        {Math.floor(product.term / 12)}yr
                      </Button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wider">Refinance / Payoff Amount (USD)</label>
                  <Input
                    type="number"
                    value={mortgageAmount}
                    onChange={(e) => handleAmountChange(e.target.value)}
                    placeholder="Enter mortgage payoff amount"
                    className="mt-2 bg-[#0a1628] border-[#1a2d4a] text-white"
                    data-testid="mortgage-amount-input"
                  />
                </div>
                
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wider">Property Address *</label>
                  <Input
                    value={formData.property_address}
                    onChange={(e) => setFormData(prev => ({ ...prev, property_address: e.target.value }))}
                    placeholder="Full property address"
                    className="mt-2 bg-[#0a1628] border-[#1a2d4a] text-white"
                    data-testid="property-address-input"
                  />
                </div>
                
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wider">Property Type</label>
                  <select
                    value={formData.property_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, property_type: e.target.value }))}
                    className="mt-2 w-full bg-[#0a1628] border border-[#1a2d4a] text-white rounded-md p-2"
                  >
                    <option value="single_family">Single Family</option>
                    <option value="condo">Condo / Townhouse</option>
                    <option value="multi_family">Multi-Family (2-4 units)</option>
                    <option value="investment">Investment Property</option>
                  </select>
                </div>
                
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wider">Original Lender Name *</label>
                  <Input
                    value={formData.original_lender}
                    onChange={(e) => setFormData(prev => ({ ...prev, original_lender: e.target.value }))}
                    placeholder="Current mortgage holder"
                    className="mt-2 bg-[#0a1628] border-[#1a2d4a] text-white"
                    data-testid="original-lender-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-400 uppercase tracking-wider">Current Rate %</label>
                    <Input
                      type="number"
                      step="0.125"
                      value={formData.current_rate}
                      onChange={(e) => setFormData(prev => ({ ...prev, current_rate: e.target.value }))}
                      placeholder="e.g., 7.5"
                      className="mt-2 bg-[#0a1628] border-[#1a2d4a] text-white"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 uppercase tracking-wider">Current Payment</label>
                    <Input
                      type="number"
                      value={formData.current_payment}
                      onChange={(e) => setFormData(prev => ({ ...prev, current_payment: e.target.value }))}
                      placeholder="Monthly"
                      className="mt-2 bg-[#0a1628] border-[#1a2d4a] text-white"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#0d1d35] border-[#1a2d4a]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#b8860b]" />
                  Cost Sheet
                </CardTitle>
              </CardHeader>
              <CardContent>
                {costSheet ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-gradient-to-r from-[#0a1628] to-[#1a2d4a] rounded-lg border border-[#b8860b]/30">
                      <div className="text-xs text-[#b8860b] uppercase tracking-wider mb-2">New Monthly Payment</div>
                      <div className="text-3xl font-bold text-white">${parseFloat(costSheet.monthlyPayment).toLocaleString()}</div>
                      <div className="text-sm text-gray-400 mt-1">{costSheet.termYears}-year term at {costSheet.indexedAPR}% APR</div>
                      {formData.current_payment && (
                        <div className="text-sm text-green-400 mt-2">
                          Potential savings: ${(parseFloat(formData.current_payment) - parseFloat(costSheet.monthlyPayment)).toFixed(0)}/month
                        </div>
                      )}
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between py-2 border-b border-[#1a2d4a]">
                        <span className="text-gray-400">Payoff Amount</span>
                        <span className="text-white">${parseFloat(costSheet.principal).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-[#1a2d4a]">
                        <span className="text-gray-400">Total of Payments</span>
                        <span className="text-white">${parseFloat(costSheet.totalPayments).toLocaleString()}</span>
                      </div>
                    </div>
                    
                    <div className="p-3 bg-blue-900/20 border border-blue-700/50 rounded-lg">
                      <div className="text-xs text-blue-400 mb-2 font-medium">PLATFORM FEES (Fixed, Non-Performance)</div>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-400">Structuring Fee (1.5%)</span>
                          <span className="text-white">${parseFloat(costSheet.structuringFee).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">PAS Enhancement (1%)</span>
                          <span className="text-white">${parseFloat(costSheet.pasEnhancementFee).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Admin + Settlement + Title</span>
                          <span className="text-white">${(parseFloat(costSheet.adminFee) + parseFloat(costSheet.settlementFee) + parseFloat(costSheet.titleFee)).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-blue-700/50">
                          <span className="text-gray-400">Total Fees</span>
                          <span className="text-[#b8860b] font-bold">${parseFloat(costSheet.totalFees).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-3 bg-[#0a1628] rounded-lg">
                      <div className="flex items-center gap-2 text-xs text-[#b8860b] mb-2">
                        <Lock className="w-4 h-4" />
                        CREDIT ENHANCEMENT (BETH)
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">BETH Required</span>
                        <span className="text-white">{parseFloat(costSheet.bethCollateral).toLocaleString()} BETH</span>
                      </div>
                      <div className="flex justify-between text-sm mt-1">
                        <span className="text-gray-400">Collateralization</span>
                        <span className="text-white">{costSheet.collateralRatio}%</span>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button 
                        className="flex-1 bg-[#b8860b] hover:bg-[#9a7209]"
                        onClick={handleCreateOrder}
                        disabled={loading || !formData.property_address || !formData.original_lender}
                        data-testid="create-timo-btn"
                      >
                        {loading ? 'Creating...' : 'Create TIMO'}
                      </Button>
                      <Button 
                        variant="outline" 
                        className="border-[#1a2d4a] text-[#b8860b]"
                        onClick={() => printMortgageDocument(null)}
                      >
                        <Printer className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Calculator className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Select a term and enter mortgage amount to see payment details</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#b8860b]" />
                My Mortgage Obligations
              </CardTitle>
            </CardHeader>
            <CardContent>
              {orders.length > 0 ? (
                <div className="space-y-4">
                  {orders.map((order) => (
                    <div key={order.order_id} className="p-4 bg-[#0a1628] rounded-lg border border-[#1a2d4a]">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="text-white font-medium">{order.product_name}</h4>
                          <p className="text-xs text-gray-500 font-mono">{order.order_id}</p>
                        </div>
                        <span className={`px-2 py-1 text-xs rounded ${
                          order.status === 'active' ? 'bg-green-500/20 text-green-400' :
                          order.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>
                          {order.status?.toUpperCase()}
                        </span>
                      </div>
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Property</p>
                          <p className="text-white text-xs truncate">{order.property_address || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Principal</p>
                          <p className="text-white">${parseFloat(order.principal_amount || 0).toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Monthly</p>
                          <p className="text-[#b8860b]">${parseFloat(order.monthly_payment || 0).toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Term</p>
                          <p className="text-white">{Math.floor((order.term_months || 0) / 12)} yr</p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Button size="sm" variant="outline" onClick={() => printMortgageDocument(order)}>
                          <Printer className="w-4 h-4 mr-1" /> Print
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Home className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p>No mortgage obligations yet</p>
                  <p className="text-sm mt-2">Use the calculator tab to create your first TIMO</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Novation Tab */}
        {activeTab === 'novations' && (
          <div className="space-y-6">
            {/* Novation Info Card */}
            <Card className="bg-gradient-to-r from-indigo-900/30 to-[#0d1d35] border-indigo-500/30">
              <CardContent className="py-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-indigo-500/20 flex items-center justify-center">
                    <Scale className="w-6 h-6 text-indigo-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-medium">Mortgage Novation & Settlement Agreement</h3>
                    <p className="text-sm text-gray-400">
                      Refinance an existing mortgage by replacing it with a Treasury-Indexed Mortgage Obligation (TIMO).
                      <span className="text-yellow-400 ml-1">Original lender consent is REQUIRED.</span>
                    </p>
                  </div>
                  <Button 
                    className="bg-indigo-600 hover:bg-indigo-700"
                    onClick={() => setShowNovationForm(true)}
                  >
                    <FileCheck className="w-4 h-4 mr-2" />
                    New Novation
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Novation Form */}
            {showNovationForm && (
              <Card className="bg-[#0d1d35] border-indigo-500/50">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Scale className="w-5 h-5 text-indigo-400" />
                    Create Mortgage Novation Agreement
                  </CardTitle>
                  <CardDescription>
                    Replace an existing mortgage with a new TIMO
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Step 1: Original Mortgage Details */}
                  <div className="p-4 bg-[#0a1628] rounded-lg border border-[#1a2d4a]">
                    <h4 className="text-white font-medium mb-4 flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-indigo-600 text-white text-xs flex items-center justify-center">1</span>
                      Original Mortgage Details
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-gray-400">Original Lender Name *</label>
                        <Input
                          value={novationForm.original_lender_name}
                          onChange={(e) => setNovationForm(prev => ({ ...prev, original_lender_name: e.target.value }))}
                          placeholder="Current mortgage holder"
                          className="mt-1 bg-[#0a1628] border-[#1a2d4a] text-white"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-400">Lender Contact</label>
                        <Input
                          value={novationForm.original_lender_contact}
                          onChange={(e) => setNovationForm(prev => ({ ...prev, original_lender_contact: e.target.value }))}
                          placeholder="Phone or email"
                          className="mt-1 bg-[#0a1628] border-[#1a2d4a] text-white"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-400">Loan Number</label>
                        <Input
                          value={novationForm.original_loan_number}
                          onChange={(e) => setNovationForm(prev => ({ ...prev, original_loan_number: e.target.value }))}
                          placeholder="Mortgage loan number"
                          className="mt-1 bg-[#0a1628] border-[#1a2d4a] text-white"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-400">Current Interest Rate %</label>
                        <Input
                          type="number"
                          step="0.125"
                          value={novationForm.original_rate}
                          onChange={(e) => setNovationForm(prev => ({ ...prev, original_rate: e.target.value }))}
                          placeholder="e.g., 7.25"
                          className="mt-1 bg-[#0a1628] border-[#1a2d4a] text-white"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-400">Payoff / Remaining Balance *</label>
                        <Input
                          type="number"
                          value={novationForm.payoff_amount}
                          onChange={(e) => {
                            setNovationForm(prev => ({ ...prev, payoff_amount: e.target.value, original_remaining_balance: e.target.value }));
                            handleAmountChange(e.target.value);
                          }}
                          placeholder="Amount to pay off"
                          className="mt-1 bg-[#0a1628] border-[#1a2d4a] text-white"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-400">Current Monthly Payment</label>
                        <Input
                          type="number"
                          value={novationForm.original_monthly_payment}
                          onChange={(e) => setNovationForm(prev => ({ ...prev, original_monthly_payment: e.target.value }))}
                          placeholder="Current payment"
                          className="mt-1 bg-[#0a1628] border-[#1a2d4a] text-white"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Step 2: Property Details */}
                  <div className="p-4 bg-[#0a1628] rounded-lg border border-[#1a2d4a]">
                    <h4 className="text-white font-medium mb-4 flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-indigo-600 text-white text-xs flex items-center justify-center">2</span>
                      Property Details
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <label className="text-xs text-gray-400">Property Address *</label>
                        <Input
                          value={novationForm.property_address || formData.property_address}
                          onChange={(e) => setNovationForm(prev => ({ ...prev, property_address: e.target.value }))}
                          placeholder="Full property address"
                          className="mt-1 bg-[#0a1628] border-[#1a2d4a] text-white"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-400">Property Type</label>
                        <select
                          value={novationForm.property_type}
                          onChange={(e) => setNovationForm(prev => ({ ...prev, property_type: e.target.value }))}
                          className="mt-1 w-full bg-[#0a1628] border border-[#1a2d4a] text-white rounded-md p-2"
                        >
                          <option value="single_family">Single Family</option>
                          <option value="condo">Condo / Townhouse</option>
                          <option value="multi_family">Multi-Family</option>
                          <option value="investment">Investment Property</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-gray-400">Lien Position</label>
                        <select
                          value={novationForm.lien_position}
                          onChange={(e) => setNovationForm(prev => ({ ...prev, lien_position: e.target.value }))}
                          className="mt-1 w-full bg-[#0a1628] border border-[#1a2d4a] text-white rounded-md p-2"
                        >
                          <option value="first">First Lien</option>
                          <option value="second">Second Lien</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Step 3: New TIMO Terms */}
                  <div className="p-4 bg-[#0a1628] rounded-lg border border-[#1a2d4a]">
                    <h4 className="text-white font-medium mb-4 flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-indigo-600 text-white text-xs flex items-center justify-center">3</span>
                      New TIMO Terms
                    </h4>
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      {TIMO_PRODUCTS.map((product) => (
                        <Button
                          key={product.id}
                          variant={selectedProduct?.id === product.id ? 'default' : 'outline'}
                          className={selectedProduct?.id === product.id ? 'bg-indigo-600' : 'border-[#1a2d4a] text-gray-400'}
                          onClick={() => handleProductSelect(product)}
                          size="sm"
                        >
                          {Math.floor(product.term / 12)}yr @ {product.baseAPR}%
                        </Button>
                      ))}
                    </div>
                    
                    {costSheet && (
                      <div className="grid grid-cols-3 gap-4 p-3 bg-indigo-900/20 rounded-lg">
                        <div className="text-center">
                          <p className="text-xs text-gray-400">New Monthly</p>
                          <p className="text-lg font-bold text-indigo-400">${parseFloat(costSheet.monthlyPayment).toLocaleString()}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-gray-400">Indexed APR</p>
                          <p className="text-lg font-bold text-white">{costSheet.indexedAPR}%</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-gray-400">BETH Required</p>
                          <p className="text-lg font-bold text-[#b8860b]">{parseFloat(costSheet.bethCollateral).toLocaleString()}</p>
                        </div>
                      </div>
                    )}
                    
                    {novationForm.original_monthly_payment && costSheet && (
                      <div className="mt-3 p-2 bg-green-900/20 border border-green-700/50 rounded text-center">
                        <p className="text-green-400 text-sm">
                          Potential Monthly Savings: <strong>${(parseFloat(novationForm.original_monthly_payment) - parseFloat(costSheet.monthlyPayment)).toFixed(2)}</strong>
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Lender Consent Warning */}
                  <div className="p-4 bg-yellow-900/20 border border-yellow-700/50 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-yellow-200">
                        <strong>Lender Consent Required:</strong> This agreement will NOT be effective until the original 
                        lender signs the consent section. You will need to send this document to your current mortgage 
                        holder for their signature.
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3">
                    <Button 
                      className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                      onClick={createNovationAgreement}
                      disabled={loading || !novationForm.original_lender_name || !costSheet}
                    >
                      {loading ? 'Creating...' : (
                        <>
                          <FileCheck className="w-4 h-4 mr-2" />
                          Create Novation Agreement
                        </>
                      )}
                    </Button>
                    <Button 
                      variant="outline"
                      className="border-[#1a2d4a] text-gray-400"
                      onClick={() => setShowNovationForm(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Existing Novations */}
            <Card className="bg-[#0d1d35] border-[#1a2d4a]">
              <CardHeader>
                <CardTitle className="text-white">My Mortgage Novation Agreements</CardTitle>
              </CardHeader>
              <CardContent>
                {novations.length > 0 ? (
                  <div className="space-y-4">
                    {novations.map((novation) => (
                      <div key={novation.agreement_id} className="p-4 bg-[#0a1628] rounded-lg border border-[#1a2d4a]">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h4 className="text-white font-medium">Mortgage Novation Agreement</h4>
                            <p className="text-xs text-gray-500 font-mono">{novation.agreement_id}</p>
                          </div>
                          <span className={`px-2 py-1 text-xs rounded ${
                            novation.status === 'fully_executed' ? 'bg-green-500/20 text-green-400' :
                            novation.status === 'pending_signatures' ? 'bg-yellow-500/20 text-yellow-400' :
                            novation.status === 'pending_lender_consent' ? 'bg-orange-500/20 text-orange-400' :
                            'bg-gray-500/20 text-gray-400'
                          }`}>
                            {novation.status?.replace(/_/g, ' ').toUpperCase()}
                          </span>
                        </div>
                        <div className="grid grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-gray-500">Original Lender</p>
                            <p className="text-white">{novation.original_lender_name}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Payoff Amount</p>
                            <p className="text-white">${parseFloat(novation.payoff_amount || 0).toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">New Monthly</p>
                            <p className="text-indigo-400">${parseFloat(novation.new_monthly_payment || 0).toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">New Term</p>
                            <p className="text-white">{Math.floor((novation.new_term_months || 0) / 12)} yr</p>
                          </div>
                        </div>
                        <div className="text-xs text-gray-500 mt-2 truncate">
                          Property: {novation.property_address}
                        </div>
                        {novation.consent_email_sent_to && (
                          <div className="text-xs text-blue-400 mt-1 flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            Consent request sent to: {novation.consent_email_sent_to}
                          </div>
                        )}
                        <div className="flex gap-2 mt-3 flex-wrap">
                          <Button size="sm" variant="outline" onClick={() => printMortgageNovationDocument(novation)}>
                            <Printer className="w-4 h-4 mr-1" /> Print Agreement
                          </Button>
                          <Button 
                            size="sm" 
                            className="bg-indigo-600 hover:bg-indigo-700"
                            onClick={() => generateLenderPackage(novation)}
                          >
                            <Package className="w-4 h-4 mr-1" /> Generate Lender Package
                          </Button>
                          {novation.status === 'pending_lender_consent' && (
                            <Button 
                              size="sm" 
                              className="bg-orange-600 hover:bg-orange-700"
                              onClick={() => { setSelectedNovation(novation); setShowConsentModal(true); }}
                            >
                              <Send className="w-4 h-4 mr-1" /> Request Consent
                            </Button>
                          )}
                          {novation.status === 'pending_signatures' && (
                            <Button 
                              size="sm" 
                              className="bg-indigo-600 hover:bg-indigo-700"
                              onClick={() => {
                                toast.info('E-Signature workflow: Document ready for signing. In production, this would open DocuSign or similar e-signature service.');
                                // Mark as signed (placeholder for actual e-signature integration)
                                setTimeout(() => {
                                  toast.success('Signature captured! Document marked as signed.');
                                }, 1500);
                              }}
                            >
                              <Pen className="w-4 h-4 mr-1" /> Sign
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <Scale className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p>No mortgage novation agreements yet</p>
                    <p className="text-sm mt-2">Click &quot;New Novation&quot; to refinance an existing mortgage</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Consent Request Modal */}
        {showConsentModal && selectedNovation && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="max-w-md w-full bg-[#0d1d35] border-[#1a2d4a]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Mail className="w-5 h-5 text-orange-400" />
                  Send Lender Consent Request
                </CardTitle>
                <CardDescription>
                  Send an email to the original lender requesting their consent for this novation agreement.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 bg-[#0a1628] rounded-lg">
                  <p className="text-xs text-gray-500">Agreement</p>
                  <p className="text-white font-mono text-sm">{selectedNovation.agreement_id}</p>
                  <p className="text-xs text-gray-400 mt-1">{selectedNovation.original_lender_name}</p>
                </div>
                
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wider">
                    Lender Email Address *
                  </label>
                  <Input
                    type="email"
                    value={lenderEmail}
                    onChange={(e) => setLenderEmail(e.target.value)}
                    placeholder="lender@bank.com"
                    className="mt-2 bg-[#0a1628] border-[#1a2d4a] text-white"
                  />
                </div>
                
                <div className="p-3 bg-orange-900/20 border border-orange-700/50 rounded-lg">
                  <p className="text-xs text-orange-200">
                    The lender will receive a professional email with a secure link to review and sign the consent form.
                  </p>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    className="flex-1 bg-orange-600 hover:bg-orange-700"
                    onClick={() => sendConsentRequest(selectedNovation)}
                    disabled={sendingEmail || !lenderEmail}
                  >
                    {sendingEmail ? 'Sending...' : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Request
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    className="border-[#1a2d4a] text-gray-400"
                    onClick={() => { setShowConsentModal(false); setSelectedNovation(null); setLenderEmail(''); }}
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default MortgageRefinance;
